/**
 * This module manages the users model of the API
 *
 * @file
 * @author Benjamin Listwon <ben@thebrandingfarm.com>
 * @author Jason Haddix <jason@thebrandingfarm.com>
 */

import api from '@/api'
import {
    VUEX_ONBOARDING_PASSWORD_RESET_REQUEST,
    VUEX_ONBOARDING_PASSWORD_RESET_REQUEST_FAILURE,
    VUEX_ONBOARDING_PASSWORD_RESET_REQUEST_SUCCESS,

    VUEX_ONBOARDING_REQUEST_ACCESS_REQUEST,
    VUEX_ONBOARDING_REQUEST_ACCESS_REQUEST_FAILURE,
    VUEX_ONBOARDING_REQUEST_ACCESS_REQUEST_SUCCESS,
    VUEX_ONBOARDING_REQUEST_ACCESS_START,
    VUEX_ONBOARDING_REQUEST_ACCESS_END,

    VUEX_ONBOARDING_REQUEST_EMAIL_VERIFY_REQUEST,
    VUEX_ONBOARDING_REQUEST_EMAIL_VERIFY_REQUEST_FAILURE,
    VUEX_ONBOARDING_REQUEST_EMAIL_VERIFY_REQUEST_SUCCESS,


    VUEX_ONBOARDING_SET_PASSWORD_REQUEST,
    VUEX_ONBOARDING_SET_PASSWORD_REQUEST_FAILURE,
    VUEX_ONBOARDING_SET_PASSWORD_REQUEST_SUCCESS,

    VUEX_ONBOARDING_VERIFY_EMAIL_REQUEST,
    VUEX_ONBOARDING_VERIFY_EMAIL_REQUEST_FAILURE,
    VUEX_ONBOARDING_VERIFY_EMAIL_REQUEST_SUCCESS,
    VUEX_ONBOARDING_VERIFY_EMAIL_START,


    VUEX_ONBOARDING_SET_RESET_PASSWORD_STATUS,

    VUEX_ONBOARDING_UPDATE_PROP,
    VUEX_ONBOARDING_UPDATE_PROP_EMAIL_RESET,
    VUEX_ONBOARDING_UPDATE_PROP_REQUEST,
    VUEX_ONBOARDING_UPDATE_PROP_REQUEST_FAILURE,
    VUEX_ONBOARDING_UPDATE_PROP_REQUEST_SUCCESS,


    VUEX_ONBOARDING_RESET_STATE,
} from '@/store/constants/onboarding'



import {
    VUEX_USER_CREATE_ROLE_REQUEST,
} from '@/store/constants/users'

import {
    VUEX_UI_SHOW_REQUEST_ACCESS_OVERLAY,
} from '@/store/constants/ui'

import {pick, omit} from 'lodash-es'
import router from '@/router'

const initialUserState = {
    access_type: null,
    email: '',
    employee_type: 1,
    first_name: '',
    last_name: '',
    password: '',
    permissions: [],
    roles: [],
    requestor_departments: [],
    status: null,
    title: '',
    emailResetEmail: '',
}

const initialOnboardingState = {
    errorsRequestVerifyEmail: [],
    errorsPasswordReset: [],
    errorsRequestAccess: [],
    errorsSetPassword: [],
    errorsVerifyEmail: [],
    isEmailVerified: false,
    isPasswordReset: false,
    isResettingPassword: false,
    isSavingUser: false,
    isUserCreated: false,
    user: {...initialUserState},
    wasEmailVerificationSent: false,
    emailResetEmail: '',
}

/**
 * Users module state
 * @const
 */
const state = {
    ...initialOnboardingState
}

/**
 * Routing module getters
 * @const
 */
const getters = {
    hasOnboardingErrorsRequestVerifyEmail: (state) => {
        return state.errorsRequestVerifyEmail.length > 0
    },
    hasOnboardingErrorsPasswordReset: (state) => {
        return state.errorsPasswordReset.length > 0
    },
    hasOnboardingErrorsRequestAccess: (state) => {
        return state.errorsRequestAccess.length > 0
    },
    hasOnboardingErrorsSetPassword: (state) => {
        return state.errorsSetPassword.length > 0
    },
    hasOnboardingErrorsVerifyEmail: (state) => {
        return state.errorsVerifyEmail.length > 0
    },
}

/**
 * Users module action definitions
 * @const
 */
const actions = {
    /**
     * Reset User Password
     * Params :
     user
     * @function
     */
    [VUEX_ONBOARDING_PASSWORD_RESET_REQUEST]: ({commit}, payload) => {
        commit(VUEX_ONBOARDING_PASSWORD_RESET_REQUEST)

        api.get(`auth/request_password_reset?email=${payload}`).then((response) => {
            commit(VUEX_ONBOARDING_PASSWORD_RESET_REQUEST_SUCCESS, response.data)
        }).catch((err) => {
            commit(VUEX_ONBOARDING_PASSWORD_RESET_REQUEST_FAILURE, err.response.data.error)
            console.error(err)
        })
    },

    /**
     * Set User Password
     * Params :
     user
     * @function
     */
    [VUEX_ONBOARDING_SET_PASSWORD_REQUEST]: ({commit}, payload) => {
        commit(VUEX_ONBOARDING_SET_PASSWORD_REQUEST)

        api.post(`auth/reset_password/${payload.userId}/${payload.token}`, {password: payload.password})
            .then((response) => {
                commit(VUEX_ONBOARDING_SET_PASSWORD_REQUEST_SUCCESS, response.data)
            }).catch((err) => {
                commit(VUEX_ONBOARDING_SET_PASSWORD_REQUEST_FAILURE, err.response.data.error)
                console.error(err)
            })
    },

    /**
     * Request User Access
     * Params :
     user
     * @function
     */
    [VUEX_ONBOARDING_REQUEST_ACCESS_REQUEST]: ({commit, dispatch}, payload) => {
        commit(VUEX_ONBOARDING_REQUEST_ACCESS_REQUEST)
        let userData = {}
        let userProvidedData
        const coreFields = ['cell', 'email', 'ext', 'first_name', 'last_name', 'title']

        Object.assign(userData, pick(payload, coreFields))
        userProvidedData = omit(payload, coreFields.concat(['status', 'password']))

        userData.user_provided_data = JSON.stringify(userProvidedData)

        api.post(`users/?source_of_request=onboarding`, userData).then((response) => {
            api.get(`auth/request_email_verification?email=${userData.email}`)

            commit(VUEX_ONBOARDING_REQUEST_ACCESS_REQUEST_SUCCESS, response.data)
            dispatch(VUEX_ONBOARDING_REQUEST_ACCESS_END)
        }).catch((err) => {
            commit(VUEX_ONBOARDING_REQUEST_ACCESS_REQUEST_FAILURE, err.response.data.errors)
        })
    },

    /**
     * Request User Email Verification
     * Params :
     user
     * @function
     */
    [VUEX_ONBOARDING_REQUEST_EMAIL_VERIFY_REQUEST]: ({commit}, payload) => {
        commit(VUEX_ONBOARDING_REQUEST_EMAIL_VERIFY_REQUEST)

        api.get(`auth/request_email_verification?email=${payload}`).then((response) => {
            commit(VUEX_ONBOARDING_REQUEST_EMAIL_VERIFY_REQUEST_SUCCESS, response.data)
        }).catch((err) => {
            commit(VUEX_ONBOARDING_REQUEST_EMAIL_VERIFY_REQUEST_FAILURE, err.response.data.error)
            console.error(err)
        })
    },

    /**
     * Verify Email Address
     * Params :
     user
     * @function
     */
    [VUEX_ONBOARDING_VERIFY_EMAIL_REQUEST]: ({commit}, payload) => {
        commit(VUEX_ONBOARDING_VERIFY_EMAIL_REQUEST)

        api.get(`auth/verify_email/${payload.userId}/${payload.token}`).then((response) => {
            commit(VUEX_ONBOARDING_VERIFY_EMAIL_REQUEST_SUCCESS, response.data)
        }).catch((err) => {
            commit(VUEX_ONBOARDING_VERIFY_EMAIL_REQUEST_FAILURE, err.response.data.error)
            console.error(err)
        })
    },

    [VUEX_ONBOARDING_REQUEST_ACCESS_START]: ({commit, dispatch}) => {
        commit(VUEX_ONBOARDING_REQUEST_ACCESS_START)
        commit(VUEX_ONBOARDING_RESET_STATE)
        dispatch(VUEX_UI_SHOW_REQUEST_ACCESS_OVERLAY)

        //GLOBAL ACTION PROMPT: Pass array of optional states to watch
        var stateChangeWatchers = [
            ['onboarding', 'user']
        ]
        dispatch('VUEX_GLOBAL_ACTION_PROMPT_SET_STATE_CHANGE_WATCHERS', {stateChangeWatchers: stateChangeWatchers})
    },

    [VUEX_ONBOARDING_REQUEST_ACCESS_END]: ({commit}, params) => {
        commit(VUEX_ONBOARDING_REQUEST_ACCESS_END)
        if(params && params.hasOwnProperty('gotoLogin')) {
            router.push({name: 'login'})
        }
    },

    [VUEX_ONBOARDING_UPDATE_PROP]: ({commit}, prop) => {
        commit(VUEX_ONBOARDING_UPDATE_PROP, prop)
    },
    [VUEX_ONBOARDING_UPDATE_PROP_EMAIL_RESET]: ({commit}, prop) => {
        commit(VUEX_ONBOARDING_UPDATE_PROP_EMAIL_RESET, prop)
    },

    [VUEX_ONBOARDING_SET_RESET_PASSWORD_STATUS]: ({commit}) => {
        commit(VUEX_ONBOARDING_SET_RESET_PASSWORD_STATUS)
    },

    [VUEX_ONBOARDING_VERIFY_EMAIL_START]: ({commit}) => {
        commit(VUEX_ONBOARDING_VERIFY_EMAIL_START)
        commit(VUEX_ONBOARDING_RESET_STATE)
    },

    [VUEX_ONBOARDING_RESET_STATE]: ({commit}) => {
        commit(VUEX_ONBOARDING_RESET_STATE)
    },

}

/**
 * Onboarding module mutation definitions
 * @const
 */
const mutations = {
    /////////////////////////////////////////////////////////////////////////////////
    [VUEX_ONBOARDING_PASSWORD_RESET_REQUEST]: (state) => {
        state.errorsPasswordReset = []
        state.isResettingPassword = true
        state.isPasswordReset = false
    },

    [VUEX_ONBOARDING_PASSWORD_RESET_REQUEST_SUCCESS]: (state) => {
        state.errorsPasswordReset = []
        state.isResettingPassword = false
        state.isPasswordReset = true
    },

    [VUEX_ONBOARDING_PASSWORD_RESET_REQUEST_FAILURE]: (state, payload) => {
        state.errorsPasswordReset = [payload]
        state.isResettingPassword = false
        state.isPasswordReset = false
    },





    /////////////////////////////////////////////////////////////////////////////////
    [VUEX_ONBOARDING_REQUEST_ACCESS_REQUEST]: (state) => {
        state.errorsRequestAccess = []
    },

    [VUEX_ONBOARDING_REQUEST_ACCESS_REQUEST_SUCCESS]: (state) => {
        state.errorsRequestAccess = []
        state.isUserCreated = true
    },

    [VUEX_ONBOARDING_REQUEST_ACCESS_REQUEST_FAILURE]: (state, payload = {}) => {
        const errors = []

        Object.keys(payload).forEach(
            key => errors.push(payload[key])
        )

        if (errors) {
            state.errorsRequestAccess = errors.join('')
        } else {
            state.errorsRequestAccess = []
        }
    },

    [VUEX_ONBOARDING_REQUEST_ACCESS_START]: () => {
    },

    [VUEX_ONBOARDING_REQUEST_ACCESS_END]: () => {
        Object.assign(state, omit('isUserCreated', initialOnboardingState))
    },






    /////////////////////////////////////////////////////////////////////////////////
    [VUEX_ONBOARDING_REQUEST_EMAIL_VERIFY_REQUEST]: (state) => {
        state.errorsVerifyEmail = []
    },

    [VUEX_ONBOARDING_REQUEST_EMAIL_VERIFY_REQUEST_SUCCESS]: (state) => {
        state.errorsVerifyEmail = []
        state.wasEmailVerificationSent = true
    },

    [VUEX_ONBOARDING_REQUEST_EMAIL_VERIFY_REQUEST_FAILURE]: (state, payload) => {
        state.errorsVerifyEmail = [payload]
        state.wasEmailVerificationSent = false
    },



    /////////////////////////////////////////////////////////////////////////////////
    [VUEX_ONBOARDING_SET_PASSWORD_REQUEST]: (state) => {
        state.errorsSetPassword = []
        state.isResettingPassword = true
        state.isPasswordReset = false
    },

    [VUEX_ONBOARDING_SET_PASSWORD_REQUEST_SUCCESS]: (state) => {
        state.errorsSetPassword = []
        state.isResettingPassword = false
        state.isPasswordReset = true

    },

    [VUEX_ONBOARDING_SET_PASSWORD_REQUEST_FAILURE]: (state, payload = {}) => {
        state.errorsSetPassword = [payload]
        state.isResettingPassword = false
        state.isPasswordReset = false
    },




    [VUEX_ONBOARDING_VERIFY_EMAIL_START]: () => {
    },




    /////////////////////////////////////////////////////////////////////////////////
    [VUEX_ONBOARDING_VERIFY_EMAIL_REQUEST]: (state) => {
        state.errorsVerifyEmail = []
    },

    [VUEX_ONBOARDING_VERIFY_EMAIL_REQUEST_SUCCESS]: (state) => {
        state.errorsVerifyEmail = []
        state.isEmailVerified = true
    },

    [VUEX_ONBOARDING_VERIFY_EMAIL_REQUEST_FAILURE]: (state, payload) => {
        state.errorsVerifyEmail = [payload]
        state.isEmailVerified = false
    },





    /////////////////////////////////////////////////////////////////////////////////

    [VUEX_ONBOARDING_SET_RESET_PASSWORD_STATUS]: (state) => {
        state.isResettingPassword = false
        state.isPasswordReset = false
    },



    /////////////////////////////////////////////////////////////////////////////////
    [VUEX_ONBOARDING_UPDATE_PROP_REQUEST]: (state) => {
        state.isSavingUser = true
    },

    [VUEX_ONBOARDING_UPDATE_PROP_REQUEST_SUCCESS]: (state) => {
        state.isSavingUser = false
    },

    [VUEX_ONBOARDING_UPDATE_PROP_REQUEST_FAILURE]: (state) => {
        state.isSavingUser = false
    },

    [VUEX_ONBOARDING_UPDATE_PROP]: (state, prop) => {
        if (prop.access_type) {
            if (prop.access_type === SILA_USER_ACCESS_TYPE_EXTERNAL) {
                delete prop.roles
                delete prop.permissions
                state.user.roles = []
                state.user.permissions = []
            } else {
                delete prop.requestor_departments
                state.user.requestor_departments = []
            }
        }

        state.user = Object.assign({}, state.user, prop)
    },

    [VUEX_ONBOARDING_UPDATE_PROP_EMAIL_RESET]: (state, payload) => {
        state.emailResetEmail = payload.email
        state.isPasswordReset = false
    },


    [VUEX_ONBOARDING_RESET_STATE]: (state) => {
        Object.assign(state, initialOnboardingState)
    },

}

/**
 * The Onboarding module of our Vuex store.
 * @module store/modules/onboarding
 */
export default {
    state,
    getters,
    actions,
    mutations,
}
